import { Scene } from 'phaser';
import { BotaoArcade } from '../components/BotaoArcade';
import { desenharFaixaInferior, desenharFundo, desenharLogoArgoX } from '../components/cenario';
import { GAME, SCENES, TEX } from '../config/constants';
import { ARCADE, ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { botoesDoAguarde, destinoDaFase, FASES, faseDoAtalho, mensagemDoAguarde } from '../services/fluxo';
import { Sessao } from '../services/Sessao';
import { t } from '../services/textos';

const CENTRO_X = 639.5;
const BOTOES = [
    { chave: 'insights', texto: 'aguarde.botao_insights', icone: TEX.ICON_POST_IT, x: 217, cena: SCENES.MURAL },
    { chave: 'materiais', texto: 'aguarde.botao_materiais', icone: TEX.ICON_MATERIAIS, x: 643, cena: SCENES.MATERIAIS },
    { chave: 'ranking', texto: 'aguarde.botao_ranking', icone: TEX.ICON_TROFEU, x: 1069, cena: SCENES.RANKING }
];

/**
 * 2. Aguarde (Figma, quadros 44/3.2): estado intermediário entre atividades
 * (`RN-AGU-01`). Quem move o participante para uma fase é o facilitador.
 */
export class AguardeScene extends Scene {
    constructor () {
        super(SCENES.AGUARDE);
    }

    init (dados = {}) {
        this.concluida = dados.concluida ?? null;
    }

    async create () {
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }

        const fonte = this.registry.get('fonte');
        if (this.concluida) fonte.registrarConclusao(this.concluida);

        // Fase aberta e ainda não concluída: o Aguarde oferece voltar a ela, em vez de só esperar.
        const emAndamento = fonte.faseEmAndamento?.() ?? null;

        desenharFundo(this, { logoY: 129 });
        desenharLogoArgoX(this, 236);
        desenharFaixaInferior(this);
        if (!emAndamento) this.desenharTitulo();

        // RI-AGU-03: fase liberada pelo facilitador troca a tela.
        const cancelar = fonte.aoLiberarFase((fase) => this.entrarNaFase(fase));
        this.events.once('shutdown', cancelar);

        if (Sessao.ehTeste) this.montarCaminhoOculto();

        const estado = await fonte.obterEstadoAguarde();
        if (!this.scene.isActive()) return;

        if (emAndamento) {
            new BotaoArcade(this, CENTRO_X, 405, {
                rotulo: t('aguarde.voltar_atividade'), largura: 556, altura: 90,
                aoClicar: () => this.entrarNaFase(emAndamento)
            });
        } else {
            this.add.text(CENTRO_X, 466, mensagemDoAguarde(estado), ARCADE_TEXT.subtitulo).setOrigin(0.5);
        }
        this.desenharProgresso(estado, emAndamento ? 530 : 557);
        this.desenharBotoes(estado);
    }

    /** "AGUARDE" branco com a sombra verde em três degraus, como no Figma. */
    desenharTitulo () {
        const y = 355;
        [5, 3, 1].forEach((dy) => {
            this.add.text(CENTRO_X + 3, y + dy, t('aguarde.titulo'), { ...ARCADE_TEXT.tituloGigante, color: ARCADE.VERDE })
                .setOrigin(0.5);
        });
        this.add.text(CENTRO_X, y, t('aguarde.titulo'), ARCADE_TEXT.tituloGigante).setOrigin(0.5);
    }

    desenharProgresso ({ concluidas, total }, y = 557) {
        const lado = 20, vao = 12, fio = 2.57;
        const topo = y - 7;
        const rotulo = this.add.text(0, y, `${concluidas}/${total}`, ARCADE_TEXT.progresso).setOrigin(0, 0.5);

        const larguraQuadros = total * lado + (total - 1) * vao;
        const inicio = CENTRO_X - (rotulo.width + 12.85 + larguraQuadros) / 2;
        rotulo.x = inicio;

        const g = this.add.graphics();
        for (let i = 0; i < total; i++) {
            const x = inicio + rotulo.width + 12.85 + i * (lado + vao);
            if (i < concluidas) {
                g.fillStyle(ARCADE_HEX.LIMA, 1).fillRect(x, topo, lado, lado);
            } else {
                g.lineStyle(fio, ARCADE_HEX.LIMA, 1)
                    .strokeRect(x + fio / 2, topo + fio / 2, lado - fio, lado - fio);
            }
        }
    }

    desenharBotoes (estado) {
        const regras = botoesDoAguarde(estado);

        BOTOES.forEach(({ chave, texto, icone, x, cena }) => {
            if (!regras[chave].visivel) return;
            new BotaoArcade(this, x, 734, {
                rotulo: t(texto), icone,
                variante: 'apagado',                  // RI-AGU-01 / RI-AGU-02: indisponível fica visível e inativo
                habilitado: regras[chave].ativo,
                aoClicar: () => this.scene.start(cena, { voltarPara: SCENES.AGUARDE })
            });
        });
    }

    entrarNaFase (fase) {
        const destino = destinoDaFase(fase);
        if (destino) this.scene.start(destino.cena, destino.dados);
    }

    /**
     * DP-TF-005: só a sessão de teste chega aqui. Teclas S/P/L/V e, para
     * tablet sem teclado, as mesmas letras no canto superior direito.
     */
    montarCaminhoOculto () {
        const aoTeclar = (evento) => {
            const fase = faseDoAtalho(evento.key, Sessao.atual);
            if (fase) this.entrarNaFase(fase);
        };
        this.input.keyboard?.on('keydown', aoTeclar);
        this.events.once('shutdown', () => this.input.keyboard?.off('keydown', aoTeclar));

        const letras = Object.entries(FASES).map(([fase, { tecla }]) => ({ fase, tecla }));
        letras.forEach(({ fase, tecla }, i) => {
            const x = GAME.WIDTH - 40 - (letras.length - 1 - i) * 52;
            this.criarAtalho(x, 36, tecla, () => this.entrarNaFase(fase));
        });

        this.criarAtalho(64, 36, 'SAIR', () => {
            Sessao.encerrar();
            this.game.events.emit('sessao:encerrada');
            this.scene.start(SCENES.LOGIN);
        }, 88);
    }

    criarAtalho (x, y, texto, aoClicar, largura = 44) {
        const fundo = this.add.rectangle(x, y, largura, 44, ARCADE_HEX.GRAFITE, 0.6)
            .setStrokeStyle(2, ARCADE_HEX.LIMA, 0.6)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', aoClicar);
        this.add.text(x, y - 1, texto, ARCADE_TEXT.atalho).setOrigin(0.5).setAlpha(0.8);
        return fundo;
    }
}
